import { Document } from '@contentful/rich-text-types';
import {
  Accordion as AccordionBtn,
  ContentfulRichText as RichText,
  ImageGallery,
  PDFDisplay as PDFViewer,
  SanityRichText,
} from '@loomstateorg/shared-frontend';
import { ContentImage } from '../../types';

interface ContentSectionProps {
  children?: React.ReactNode;

  id?: string;
  title?: string;
  subtitle?: string;
  subtitlePosition?: 'left' | 'top';
  content?: Document | any[]; // Contentful Document or Sanity Portable Text array
  tableHeadColor?: string;
  tableHeadTextColor?: string;
  tableBodyColor?: string;
  tableBodyTextColor?: string;
  textContentWidth?: '1/4' | '1/3' | '1/2' | '2/3' | '3/4' | 'full';
  images?: ContentImage[];
  imageLayout?: 'inline' | 'grid';
  imagePosition?: 'left' | 'right' | 'centered';
  imageWidth?: '1/4' | '1/3' | '1/2' | 'full';
  className?: string;
  displaySectionAsAccordion?: boolean;
  accordionTitle?: string;
  pdfFile?: any;
  accordionButtonClassName?: string;
}

const WIDTH_CLASSES = {
  '1/4': '2xl:w-1/4',
  '1/3': '2xl:w-1/3',
  '1/2': '2xl:w-1/2',
  '2/3': '2xl:w-2/3',
  '3/4': '2xl:w-3/4',
  full: 'w-full',
} as const;

const MOBILE_IMAGE_MAX_CLASSES = {
  '1/4': 'max-2xl:max-w-[220px]',
  '1/3': 'max-2xl:max-w-[280px]',
  '1/2': 'max-2xl:max-w-[340px]',
  '2/3': 'max-2xl:max-w-[400px]',
  '3/4': 'max-2xl:max-w-[440px]',
  full: 'max-2xl:max-w-[360px]',
} as const;

type WidthOption = keyof typeof WIDTH_CLASSES;

const getWidthOption = (width: string | undefined, fallback: WidthOption): WidthOption =>
  width && width in WIDTH_CLASSES ? (width as WidthOption) : fallback;

const ContentSection: React.FC<ContentSectionProps> = ({
  accordionButtonClassName = 'font-medium text-16 py-20 px-16 rounded-md',
  id,
  title,
  subtitle,
  subtitlePosition = 'top',
  content,
  tableHeadColor,
  tableHeadTextColor,
  tableBodyColor,
  tableBodyTextColor,
  textContentWidth = 'full',
  images = [],
  imageLayout = 'inline',
  imagePosition = 'right',
  imageWidth = 'full',
  className,
  displaySectionAsAccordion = false,
  accordionTitle,
  pdfFile,
}) => {
  const resolvedTextContentWidth = getWidthOption(textContentWidth, 'full');
  const resolvedImageWidth = getWidthOption(imageWidth, 'full');

  const imageOrderClass =
    imagePosition === 'left' ? 'order-1 2xl:order-none' : imagePosition === 'right' ? 'order-3 2xl:order-none' : 'order-4 2xl:order-none';

  const renderImages = (position: typeof imagePosition) => {
    if (imagePosition !== position || !images.length) return null;

    const imagesContainerClass = [
      'w-full max-2xl:mx-auto',
      imageOrderClass,
      position !== 'centered' && 'self-start',
      position !== 'centered' && resolvedImageWidth !== 'full' && '2xl:shrink-0',
      position !== 'centered' && WIDTH_CLASSES[resolvedImageWidth],
      MOBILE_IMAGE_MAX_CLASSES[resolvedImageWidth],
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={imagesContainerClass}>
        <ImageGallery
          images={images}
          layout={imageLayout}
          className="[&_img]:mx-auto [&_img]:h-auto [&_img]:max-w-full [&_img]:object-contain"
        />
      </div>
    );
  };

  const renderPDF = () => {
    if (!pdfFile) return null;

    return (
      <div className={`mt-8 w-full ${WIDTH_CLASSES[resolvedTextContentWidth]}`}>
        <PDFViewer pdf={pdfFile} />
      </div>
    );
  };

  // Check if content is Sanity Portable Text (array) or Contentful Document
  const isSanityContent = Array.isArray(content);

  const renderContent = () => {
    if (!content) return null;

    if (subtitlePosition === 'left') {
      return (
        <div className="order-2 min-w-0 w-full 2xl:order-none">
          <div className="flex min-w-0 flex-col 2xl:flex-row 2xl:gap-16">
            {subtitle && (
              <div className="mb-6 2xl:mb-0">
                <p className="text-lg leading-normal">{subtitle}</p>
              </div>
            )}
            <div className={`min-w-0 ${WIDTH_CLASSES[resolvedTextContentWidth]}`}>
              {isSanityContent ? (
                <SanityRichText
                  content={content}
                  className="font-primaryThin"
                  tableHeadColor={tableHeadColor}
                  tableBodyColor={tableBodyColor}
                  tableHeadTextColor={tableHeadTextColor}
                  tableBodyTextColor={tableBodyTextColor}
                />
              ) : (
                <RichText
                  content={content}
                  className="font-primaryThin"
                  tableHeadColor={tableHeadColor}
                  tableBodyColor={tableBodyColor}
                  tableHeadTextColor={tableHeadTextColor}
                  tableBodyTextColor={tableBodyTextColor}
                />
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`order-2 min-w-0 w-full 2xl:order-none ${WIDTH_CLASSES[resolvedTextContentWidth]}`}>
        {title && (
          <h2 id={`content-heading-${id || 'default'}`} className="mb-6 text-4xl leading-normal">
            {title}
          </h2>
        )}
        {subtitle && <div className="mb-6 text-lg">{subtitle}</div>}
        {isSanityContent ? (
          <SanityRichText
            content={content}
            className="font-primaryThin"
            tableHeadColor={tableHeadColor}
            tableBodyColor={tableBodyColor}
            tableHeadTextColor={tableHeadTextColor}
            tableBodyTextColor={tableBodyTextColor}
          />
        ) : (
          <RichText
            content={content}
            className="font-primaryThin"
            tableHeadColor={tableHeadColor}
            tableBodyColor={tableBodyColor}
            tableHeadTextColor={tableHeadTextColor}
            tableBodyTextColor={tableBodyTextColor}
          />
        )}
      </div>
    );
  };

  const mainContent = (
    <article
      id={id}
      className={`flex flex-col items-stretch gap-8 2xl:flex-row 2xl:items-center 2xl:gap-32 ${className ?? ''}`}
      data-testid={`content-section${id ? `-${id}` : ''}`}
      aria-labelledby={title ? `content-heading-${id || 'default'}` : undefined}
      aria-label={!title && subtitle ? subtitle : undefined}
    >
      {renderImages('left')}
      {renderContent()}
      {renderImages('right')}
      {renderImages('centered')}
      {renderPDF()}
    </article>
  );

  if (displaySectionAsAccordion) {
    return (
      <AccordionBtn
        variant="plusAndMinus"
        buttonWrapperClassName="mb-20"
        iconClassName="text-primary-regular"
        closedSectionTitle={accordionTitle || subtitle}
        buttonClassName={accordionButtonClassName}
      >
        {mainContent}
      </AccordionBtn>
    );
  }

  return mainContent;
};

export default ContentSection;
